import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import {
  catchError,
  exhaustMap,
  firstValueFrom,
  map,
  Observable,
  of,
  switchMap,
} from 'rxjs';
import {
  USER_SESSION,
  USER_SESSION_LOGIN,
  USER_SESSION_PRE,
} from '../../helpers/global/global.constant';
import { AuthService } from '../../services/api/auth.service';
import { UserService } from '../../services/api/user.service';
import { StorageService } from '../../services/ui/storage.service';
import { User } from '../../interfaces/api/user.interface';
import { UserActions } from './auth.actions';
import { swalWithBootstrapButtons } from '../../helpers/ui/ui.constant';
import { TranslateService } from '@ngx-translate/core';
import {
  RESPONSE_CODES,
  TFA_CANCELLED_KEY,
  TFA_ERROR_MESSAGE_KEYS,
  TFA_INCORRECT_CODE_KEY,
  TFA_MISSING_TOKEN_KEY,
} from '../../helpers/global/auth.contants';
import { isHttpStatusError } from '../../helpers/global/http-error.helper';
import { ToastrNotificationService } from '../../services/ui/toastr-notification.service';

@Injectable()
export class AuthEffects {
  constructor(
    private actions$: Actions,
    private router: Router,
    private authService: AuthService,
    private userService: UserService,
    private storageService: StorageService,
    private translate: TranslateService,
    private notificationService: ToastrNotificationService
  ) {}

  loadUserSession$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.loadUserSession),
      exhaustMap(() => this.loadUserSession())
    )
  );

  loginUser$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.userAuthenticationRequest),
      switchMap((action) => this.loginUser(action)),
      switchMap((result) =>
        result.type === UserActions.userAuthenticationFailure.type
          ? of(result)
          : this.reloadUserSessionAfterLogin()
      )
    )
  );

  logoutUser$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.userLogout),
      switchMap(() => {
        this.authService.logout();
        return [UserActions.completeUserLogout()];
      })
    )
  );

  updateUserProfile$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.requestUserProfileUpdate),
      map((action) => {
        return action.user;
      }),
      switchMap((user) =>
        this.userService.updateProfile(user).pipe(
          map((res) =>
            UserActions.userProfileUpdateSuccess({ user: res.data })
          ),
          catchError((error) =>
            of(
              UserActions.userProfileUpdateFailure({
                message: error.message,
              })
            )
          )
        )
      )
    )
  );

  enableTwoFactorAuth$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.enableTwoFactorAuthentication),
      switchMap((action) =>
        this.userService.activateTwoFactor(action._id, action.code).pipe(
          map((res) =>
            UserActions.enableTwoFactorAuthenticationSuccess({ user: res.data })
          ),
          catchError((error) =>
            of(
              UserActions.enableTwoFactorAuthenticationFailure({
                message: `Failed to enable Two-Factor Authentication: ${error.message}`,
              })
            )
          )
        )
      )
    )
  );

  disableTwoFactorAuth$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.disableTwoFactorAuthentication),
      switchMap((action) =>
        this.userService.disableTwoFactor(action._id).pipe(
          map((res) =>
            UserActions.disableTwoFactorAuthenticationSuccess({
              user: res.data,
            })
          ),
          catchError((error) =>
            of(
              UserActions.disableTwoFactorAuthenticationFailure({
                message: `Failed to disable Two-Factor Authentication: ${error.message}`,
              })
            )
          )
        )
      )
    )
  );

  // Funciones privadas para separar lógica

  private loadUserSession() {
    const storedSession = JSON.parse(
      this.storageService.secureStorage.getItem(USER_SESSION)
    );

    if (storedSession) {
      const userType: 'cashdesk' | 'user' = storedSession.type
        ? USER_SESSION_LOGIN.CASHDESK
        : USER_SESSION_LOGIN.USER;

      const userServiceCall =
        userType === USER_SESSION_LOGIN.CASHDESK
          ? this.userService.getMeCashdesks()
          : this.userService.getMeUser();

      return userServiceCall.pipe(
        map((response) => {
          const user = response.data;
          return UserActions.userAuthenticationSuccess({ user });
        }),
        catchError((error) => {
          return of(
            UserActions.userAuthenticationFailure({
              message: `Failed to load session: ${error.message}`,
            })
          );
        })
      );
    } else {
      return of(
        UserActions.userAuthenticationFailure({
          message: 'User session not found.',
        })
      );
    }
  }

  private loginUser(
    action: ReturnType<typeof UserActions.userAuthenticationRequest>
  ) {
    const { request } = action;
  
    return this.authService.login(request).pipe(
      switchMap((response) => {
        const loggedUser = response.data;
        if (loggedUser.TFA === true) {
          return loggedUser.twoFactorToken
            ? this.completeTwoFactorLogin(loggedUser.twoFactorToken)
            : of(this.missingTokenFailure());
        }

        if (!loggedUser.token) {
          return of(this.missingTokenFailure());
        }
        this.storageService.secureStorage.setItem(
          USER_SESSION,
          JSON.stringify(loggedUser)
        );
        return of(UserActions.userAuthenticationSuccess({ user: loggedUser }));
      }),
      catchError((error) =>
        of(
          UserActions.userAuthenticationFailure({
            message: `Login failed: ${error.message}`,
          })
        )
      )
    );
  }
  

  private missingTokenFailure() {
    this.notificationService.showNotification({
      type: 'error',
      title: 'Error',
      message: TFA_MISSING_TOKEN_KEY,
    });
    return UserActions.userAuthenticationFailure({
      message: this.translate.instant(TFA_MISSING_TOKEN_KEY),
    });
  }

  private completeTwoFactorLogin(twoFactorToken: string) {
    return this.handleTFA(twoFactorToken).pipe(
      map((verifiedUser) => {
        if (!verifiedUser) {
          return UserActions.userAuthenticationFailure({
            message: this.translate.instant(TFA_CANCELLED_KEY),
          });
        }
        this.storageService.secureStorage.setItem(
          USER_SESSION,
          JSON.stringify(verifiedUser)
        );
        return UserActions.userAuthenticationSuccess({ user: verifiedUser });
      })
    );
  }

  private showValidationMessage(messageKey: string): false {
    swalWithBootstrapButtons.showValidationMessage(
      this.translate.instant(messageKey)
    );
    return false;
  }

  private async verifyTfaCode(
    twoFactorToken: string,
    tfaCode: string
  ): Promise<User | false> {
    if (!tfaCode) {
      return this.showValidationMessage(TFA_INCORRECT_CODE_KEY);
    }
    try {
      const response = await firstValueFrom(
        this.userService.verifyLogin(twoFactorToken, tfaCode)
      );
      const isVerified =
        response?.statusCode === RESPONSE_CODES.SUCCESS && !!response.data?.token;
      return isVerified
        ? response.data
        : this.showValidationMessage(TFA_INCORRECT_CODE_KEY);
    } catch (error) {
      const messageKey = isHttpStatusError(error)
        ? TFA_ERROR_MESSAGE_KEYS[error.status]
        : undefined;
      return this.showValidationMessage(messageKey ?? TFA_INCORRECT_CODE_KEY);
    }
  }

  private handleTFA(twoFactorToken: string): Observable<User | null> {
    return new Observable<User | null>((observer) => {
      swalWithBootstrapButtons
        .fire({
          title: this.translate.instant(
            'profile.setPassword.enterCodeVerification'
          ),
          input: 'text',
          showCancelButton: true,
          confirmButtonText: this.translate.instant(
            'profile.setPassword.buttons.authorize'
          ),
          showLoaderOnConfirm: true,
          preConfirm: (tfaCode: string) =>
            this.verifyTfaCode(twoFactorToken, tfaCode),
          allowOutsideClick: () => !swalWithBootstrapButtons.isLoading(),
        })
        .then((result) => {
          observer.next(result.isConfirmed ? (result.value as User) : null);
          observer.complete();
        });
    });
  }

  reloadUserSessionAfterLogin() {
    const storedSession =
      JSON.parse(this.storageService.secureStorage.getItem(USER_SESSION)) ||
      JSON.parse(this.storageService.secureStorage.getItem(USER_SESSION_PRE));

    if (storedSession) {
      const userType: 'cashdesk' | 'user' = storedSession.type
        ? USER_SESSION_LOGIN.CASHDESK
        : USER_SESSION_LOGIN.USER;

      const userServiceCall =
        userType === USER_SESSION_LOGIN.CASHDESK
          ? this.userService.getMeCashdesks()
          : this.userService.getMeUser();

      return userServiceCall.pipe(
        map((response) => {
          const user = response.data;
          this.router.navigate([``]);

          return UserActions.userAuthenticationSuccess({ user });
        }),
        catchError((error) => {
          return of(
            UserActions.userAuthenticationFailure({
              message: `Failed to load session: ${error.message}`,
            })
          );
        })
      );
    } else {
      return of(
        UserActions.userAuthenticationFailure({
          message: 'User session not found.',
        })
      );
    }
  }
}
